import { createLogger, errorFields } from '@/shared/logging';

const appLog = createLogger('app');

// When this tab last reloaded itself for a new build. Per tab, so one tab's reload
// never holds back another's.
const RELOAD_AT_KEY = 'glai:stale-build-reload-at';

// A tab that fails again this soon after reloading did not find the chunk in the new
// build either. Another reload would loop, so the failure is left to the error page.
const RELOAD_GUARD_MS = 10_000;

let reloading = false;

/** Whether this tab is already on its way to the deployed build. */
export function isReloadingForNewBuild(): boolean {
	return reloading;
}

interface StaleBuildReloadDeps {
	events: Pick<Window, 'addEventListener'>;
	storage: () => Pick<Storage, 'getItem' | 'setItem'>;
	reload: () => void;
	now: () => number;
}

/**
 * Reloads the tab onto the deployed build when a chunk it asks for is gone.
 *
 * Chunk file names carry a content hash, so a deploy replaces every one of them,
 * and a tab opened before the deploy still asks for the old names. The first page
 * that tab has not visited yet then fails to load its code, and the route boundary
 * shows the error page until the reader refreshes and picks up the new index.html.
 * Vite reports every failed dynamic import, the chunk itself and anything it
 * preloads, as `vite:preloadError`, so the refresh is done for the reader, once.
 *
 * The event is not cancelled. Cancelling it makes the import resolve to undefined
 * instead of failing, and the page would be rendered from nothing. Letting the import
 * fail, while the boundary renders the loading state, is what keeps the error page
 * from flashing up before the reload lands.
 */
export function reloadOnStaleBuild(deps: Partial<StaleBuildReloadDeps> = {}): void {
	const { events = window, storage = () => window.sessionStorage, reload = () => window.location.reload(), now = Date.now } = deps;

	events.addEventListener('vite:preloadError', (event) => {
		if (reloading || !claimReload(storage, now)) return;

		reloading = true;
		appLog.warn('chunk missing from this build; reloading onto the deployed one', errorFields(event.payload));
		reload();
	});
}

function claimReload(storage: StaleBuildReloadDeps['storage'], now: () => number): boolean {
	try {
		const store = storage();
		const reloadedAt = Number(store.getItem(RELOAD_AT_KEY));
		if (now() - reloadedAt < RELOAD_GUARD_MS) return false;

		store.setItem(RELOAD_AT_KEY, String(now()));
		return true;
	} catch {
		// Without storage a second failure cannot be told from the first, and an
		// unguarded reload could loop: the error page is the safer answer.
		return false;
	}
}

/** Test seam: forget that a reload has started. */
export function resetStaleBuildReloadForTests(): void {
	reloading = false;
}
